import React from 'react';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { Target, Users, BookOpen, Award, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-slate-50 font-['Inter',sans-serif]">
      <PublicNavbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
            Redefining <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Education</span> for the Future
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            We believe that high-quality education should be accessible, engaging, and personalized. 
            By leveraging modern AI technology and expert educators, we create learning experiences that empower students to achieve greatness.
          </motion.p>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Our Core Values</h2>
            <div className="w-24 h-1.5 bg-indigo-600 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Excellence", icon: <Award />, desc: "We strive for academic brilliance and top-tier results in everything we do.", color: "text-amber-500 bg-amber-50" },
              { title: "Innovation", icon: <Target />, desc: "Integrating AI and smart tools to provide a seamless learning journey.", color: "text-indigo-500 bg-indigo-50" },
              { title: "Accessibility", icon: <Users />, desc: "Education without borders. Learn anytime, anywhere, on any device.", color: "text-green-500 bg-green-50" },
              { title: "Integrity", icon: <CheckCircle />, desc: "Honest, transparent, and student-centric approach to teaching.", color: "text-purple-500 bg-purple-50" }
            ].map((val, i) => (
              <div key={i} className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 text-center hover:-translate-y-2 transition-transform duration-300">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${val.color}`}>
                  {React.cloneElement(val.icon, { size: 32 })}
                </div>
                <h3 className="text-xl font-black text-slate-800 mb-3">{val.title}</h3>
                <p className="text-slate-500 font-medium text-sm leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-indigo-900 text-white relative overflow-hidden">
         <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
         <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-5xl font-black text-amber-400 mb-2">10k+</p>
                <p className="text-sm font-bold tracking-widest uppercase text-indigo-200">Students</p>
              </div>
              <div>
                <p className="text-5xl font-black text-white mb-2">150+</p>
                <p className="text-sm font-bold tracking-widest uppercase text-indigo-200">Expert Faculty</p>
              </div>
              <div>
                <p className="text-5xl font-black text-white mb-2">200+</p>
                <p className="text-sm font-bold tracking-widest uppercase text-indigo-200">Courses</p>
              </div>
              <div>
                <p className="text-5xl font-black text-green-400 mb-2">95%</p>
                <p className="text-sm font-bold tracking-widest uppercase text-indigo-200">Success Rate</p>
              </div>
            </div>
         </div>
      </section>

      <PublicFooter />
    </div>
  );
}
